import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { UserRole } from "@/lib/types";
import { useAuth } from "@/hooks/useAuth";

export interface InvitedUser {
  id: string;
  email: string;
  name: string | null;
  company_name: string | null;
  roles: UserRole[];
  status: "pending" | "accepted" | "expired";
  created_at: string;
  expires_at: string;
}

export function useUserInvitations() {
  const queryClient = useQueryClient();
  const { getAccessToken } = useAuth();

  const fetchInvitedUsers = async (): Promise<InvitedUser[]> => {
    const { data, error } = await supabase
      .from("user_invitations")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching invited users:", error);
      toast.error(`Error fetching invited users: ${error.message}`);
      return [];
    }

    // Process the data to mark invitations as expired if they are past their expiration date
    // BUT keep "accepted" invitations as "accepted" even if expired
    const processedData = data.map(invitation => {
      const expiryDate = new Date(invitation.expires_at);
      const now = new Date();
      
      // Only update status to expired if it's currently pending and past expiry date
      // Don't change status if it's already accepted
      if (invitation.status === 'pending' && expiryDate < now) {
        return {
          ...invitation,
          status: 'expired' as const
        };
      }
      
      return invitation;
    });

    return processedData as InvitedUser[];
  };

  const resendInvitation = async (invitationId: string): Promise<void> => {
    try {
      // Get the invitation details
      const { data: invitation, error: fetchError } = await supabase
        .from("user_invitations")
        .select("*")
        .eq("id", invitationId)
        .single();

      if (fetchError) {
        toast.error(`Error retrieving invitation: ${fetchError.message}`);
        throw fetchError;
      }

      // Get the inviter details
      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError) {
        toast.error(`Error getting user: ${userError.message}`);
        throw userError;
      }

      // Update the expiration date (extend by 7 days from now)
      const newExpiryDate = new Date();
      newExpiryDate.setDate(newExpiryDate.getDate() + 7);
      
      const { error: updateError } = await supabase
        .from("user_invitations")
        .update({ 
          expires_at: newExpiryDate.toISOString(),
          status: 'pending' 
        })
        .eq("id", invitationId);
        
      if (updateError) {
        toast.error(`Error updating invitation: ${updateError.message}`);
        throw updateError;
      }

      // Resend the invitation email
      const { error: emailError } = await supabase.functions.invoke(
        "send-invitation-email",
        {
          body: {
            email: invitation.email,
            name: invitation.name,
            companyName: invitation.company_name,
            roles: invitation.roles,
            token: invitation.token,
            invitedBy: {
              name: userData.user?.user_metadata?.name,
              email: userData.user?.email,
            },
          },
        }
      );

      if (emailError) {
        toast.error(`Error resending invitation: ${emailError.message}`);
        throw emailError;
      }

      toast.success(`Invitation resent to ${invitation.email}`);
    } catch (error: any) {
      console.error("Error resending invitation:", error);
      throw error;
    }
  };

  const deleteInvitation = async (invitationId: string): Promise<void> => {
    const { error } = await supabase
      .from("user_invitations")
      .delete()
      .eq("id", invitationId);

    if (error) {
      toast.error(`Error deleting invitation: ${error.message}`);
      throw error;
    }

    toast.success("Invitation deleted successfully");
  };

  const markAsExpired = async (invitationId: string): Promise<void> => {
    const { error } = await supabase
      .from("user_invitations")
      .update({ status: 'expired' })
      .eq("id", invitationId);

    if (error) {
      toast.error(`Error updating invitation status: ${error.message}`);
      throw error;
    }

    toast.success("Invitation marked as expired");
  };

  const useInvitedUsersQuery = () => 
    useQuery({
      queryKey: ["invitedUsers"],
      queryFn: fetchInvitedUsers,
    });

  const useResendInvitationMutation = () => 
    useMutation({
      mutationFn: resendInvitation,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["invitedUsers"] });
      }
    });

  const useDeleteInvitationMutation = () => 
    useMutation({
      mutationFn: deleteInvitation,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["invitedUsers"] });
      }
    });
    
  const useMarkAsExpiredMutation = () => 
    useMutation({
      mutationFn: markAsExpired,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["invitedUsers"] });
      }
    });

  return {
    useInvitedUsersQuery,
    useResendInvitationMutation,
    useDeleteInvitationMutation,
    useMarkAsExpiredMutation
  };
}
